import React from 'react';
import Head from 'next/head';
import NextSeo from 'next-seo';

import { useTranslations } from '@lang';
import { SpaTreatments } from '@features/spa/pages/SpaTreatments';
import * as journeyActions from '@store/yourJourney/actions';

import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { NextPage, NextPageContext } from 'next';
import { Property } from 'shared-types/Property';
import { Router } from 'shared-routing';
import { dehydrate, DehydratedState, QueryClient } from '@tanstack/react-query';
import { getSpaTreatmentsPagePrefetch } from '@features/spa/hooks';
import { getSpaCategoriesPrefetch } from '@features/spa/hooks/useGetSpaCategories';

type SpaTreatmentsPageProps = {
  propertyTitle: string;
  socialFallbackImage: {
    url: string;
  };
  language: string;
  url: string;
  categoryId: number | null;
};

const SpaTreatmentsPage: NextPage<SpaTreatmentsPageProps> = ({
  categoryId,
  language,
  propertyTitle = '',
  socialFallbackImage = { url: '' },
  url,
}) => {
  const { t } = useTranslations();
  const pageName = t('app.spaTreatments');

  const pageTitle = propertyTitle ? `${pageName} | ${propertyTitle}` : pageName;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: socialFallbackImage && socialFallbackImage.url }],
          },
        }}
      />
      <SpaTreatments categoryId={categoryId} language={language} />
    </>
  );
};

SpaTreatmentsPage.getInitialProps = async ({
  property,
  store,
  language,
  url,
  res,
  query,
}: NextPageContext & {
  property: Property;
  store: any;
  language: string;
  url: string;
  router: any;
}): Promise<SpaTreatmentsPageProps & { dehydratedState: DehydratedState }> => {
  if (!property.isSpaIntegrated) {
    if (res) {
      res.writeHead(302, {
        Location: '/',
      });
      res.end();
    } else {
      Router.push('/');
    }
  }
  const client = new QueryClient();
  const { fromDate } = yourJourneySelectors.getParsedJourneyParams(store.getState());
  const spaDate = yourJourneySelectors.getActualSpaDateValue(store.getState());

  const spaDateToSet = spaDate || fromDate;
  const isServer = !!res;

  await Promise.all([
    store.dispatch(journeyActions.changeJourneyParam('spaDate', spaDateToSet)),
    isServer && getSpaCategoriesPrefetch(client, property.id, 'treatments'),
    getSpaTreatmentsPagePrefetch(client, property.id, language),
  ]);

  return {
    url,
    language,
    categoryId: query.category ? Number.parseInt(query.category as any, 10) : null,
    propertyTitle: property.propertyTitle,
    socialFallbackImage: property.socialFallbackImage,
    dehydratedState: dehydrate(client),
  };
};

export default SpaTreatmentsPage;
